import { useEffect, useRef } from "react";
import { Fader } from "@/components/creator/mix/MixSlider";
import { fmtDb } from "@/lib/mixDeskApi";

/** Peak meter fed by the preview's master analyser (−48 dB … 0 dB). */
function Meter({ getAnalyser, playing }) {
  const bar = useRef(null);
  useEffect(() => {
    if (!playing) { if (bar.current) bar.current.style.height = "0%"; return undefined; }
    let raf = 0;
    let buf = null;
    const tick = () => {
      const a = getAnalyser();
      if (a && bar.current) {
        buf = buf || new Float32Array(a.fftSize);
        a.getFloatTimeDomainData(buf);
        let peak = 0;
        for (let i = 0; i < buf.length; i += 1) peak = Math.max(peak, Math.abs(buf[i]));
        const db = peak > 0 ? 20 * Math.log10(peak) : -60;
        bar.current.style.height = `${Math.max(0, Math.min(100, ((db + 48) / 48) * 100))}%`;
        bar.current.dataset.hot = db > -1 ? "1" : "0";
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [getAnalyser, playing]);
  return (
    <div data-testid="mix-master-meter" className="relative h-44 w-3 overflow-hidden rounded-full bg-white/[0.08]">
      <div ref={bar} className="absolute inset-x-0 bottom-0 rounded-full bg-gradient-to-t from-emerald-400 via-amber-300 to-rose-500 transition-[height] duration-75 data-[hot='1']:shadow-[0_0_12px_rgba(244,63,94,0.8)]" style={{ height: "0%" }} />
    </div>
  );
}

/** Master bus: overall level into the mastering stage, with a live peak meter. */
export default function MasterStrip({ value, onChange, getAnalyser, playing }) {
  return (
    <div data-testid="mix-strip-master" className="flex w-[140px] shrink-0 flex-col gap-3 rounded-2xl border border-amber-300/25 bg-gradient-to-b from-amber-500/[0.10] to-[#0a0d1c]/90 p-3">
      <div className="flex items-center gap-2">
        <span className="h-6 w-1.5 shrink-0 rounded-full bg-gradient-to-b from-amber-300 to-orange-500" />
        <p className="font-display text-[13px] font-semibold">Master</p>
      </div>
      <p className="text-[11px] leading-snug text-white/50">Overall level going into mastering. The meter shows the preview.</p>
      <div className="mt-auto flex items-end justify-center gap-2">
        <Fader id="mix-level-master" label="Master level" value={value} onChange={onChange} min={-12} max={6} />
        <Meter getAnalyser={getAnalyser} playing={playing} />
      </div>
      <p data-testid="mix-level-value-master" className="text-center font-mono text-[11px] text-white/80">{fmtDb(value)}</p>
    </div>
  );
}
